






































import {
  computed,
  defineComponent,
  ref,
  Ref,
  toRefs,
  useContext,
  watch
} from '@nuxtjs/composition-api'
import { formFieldProps } from '~/constants/form'
import { Option } from '~/models/shared/types'
import { useClassifiedForm } from '~/compositions/form'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import { useNamespacedStore } from '~/compositions/store'
import { useI18n } from '~/compositions/i18n'
import { SET_CONFIG } from '~/store/modules/shared/classifieds/form/mutation-types'
import { deepJsonCopy } from '~/utils/object'
import {
  FIELD_FULL_WIDTH_COLUMNS,
  FIELD_HALF_WIDTH_COLUMNS
} from '~/constants/form/columns'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'

export default defineComponent({
  components: { CCustomSelect, FieldError },
  props: formFieldProps,
  setup(props) {
    const { specs, name } = toRefs(props)

    const selectedMake = ref<number | null>(null)
    const selectedModel = ref<number | null>(null)
    const loadingSpecs = ref<boolean>(false)

    function onValue(v: Ref<{ make: number; model: number } | null>) {
      selectedMake.value = v.value?.make || null
      selectedModel.value = v.value?.model || null
    }

    function onSpecs(_v: Ref<any>) {
      if (loadingSpecs.value) {
        loadingSpecs.value = false
      }
    }

    const { valueChanged, error } = useClassifiedForm(props, {
      onValue,
      onSpecs
    })
    const { getters, state } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
    const { t } = useI18n()
    const { store } = useContext()

    const showMakeLogos = computed(() => getters('isCarsForm'))

    const makesOptions: Ref<Option[]> = computed(() => [
      ...(specs.value.makes || []).map((o: any) => ({
        text: o.name,
        value: o.value
      }))
    ])

    const makeMustBeSelected = computed<boolean>(
      () => !specs.value.models && !selectedMake.value
    )
    const noModelsForThisMake = computed<boolean>(
      () =>
        (specs.value.models && specs.value.models.length === 0) ||
        specs.value.has_models === false
    )

    const modelsSelectIsDisabled = computed<boolean>(
      () =>
        makeMustBeSelected.value ||
        noModelsForThisMake.value ||
        loadingSpecs.value
    )

    const makeError = computed(() => error.value && error.value.make)
    const modelError = computed(() => error.value && error.value.model)

    const modelsOptions = computed<Option[]>(() => {
      if (makeMustBeSelected.value) {
        return [{ text: t('select a make first'), value: null }]
      }
      if (noModelsForThisMake.value) {
        return [{ text: t('no models found for this make'), value: null }]
      }
      return [
        { value: null, text: t('select a model') },
        ...(specs.value.models || []).map((o: any) => ({
          text: o.name,
          value: o.value
        }))
      ]
    })

    function handleMakeChange(make: number) {
      selectedModel.value = null
      loadingSpecs.value = true
      valueChanged(name.value, { make, model: selectedModel.value })
    }

    function handleModelChange(model: number) {
      valueChanged(name.value, { make: selectedMake.value, model })
    }

    const showModelImage = computed(() =>
      // only show model image for rental cars
      getters('isRentalsForm')
    )

    const hasVariant = computed(() => {
      const hasVehDetails = state.fieldsets?.find(
        fset => fset.name === 'vehicle_details'
      )
      if (hasVehDetails) {
        return hasVehDetails.fields?.filter(f => f.name === 'variant').length
      }
      return false
    })

    watch(noModelsForThisMake, val => {
      const newConf = deepJsonCopy(state.config)

      newConf.settings.fieldColumns.makemodel =
        val && hasVariant.value
          ? FIELD_HALF_WIDTH_COLUMNS
          : FIELD_FULL_WIDTH_COLUMNS

      newConf.settings.fieldColumns.variant = FIELD_HALF_WIDTH_COLUMNS

      store.commit(`${CLASSIFIED_FORM_NS}/${SET_CONFIG}`, newConf)
    })

    return {
      selectedMake,
      selectedModel,
      makesOptions,
      modelsOptions,
      modelsSelectIsDisabled,
      makeError,
      modelError,
      noModelsForThisMake,
      handleMakeChange,
      handleModelChange,
      showModelImage,
      showMakeLogos,
      loadingSpecs,
      hasVariant
    }
  }
})
